function aspectRatio(tile) {
  const cover = tile.querySelector('.pin-media');
  if (cover) {
    const [width, height = 1] = cover.style.aspectRatio.split('/').map(Number);
    return width / height;
  }
  const image = tile.matches('video, img') ? tile : tile.querySelector('img, video');
  const width = Number(image?.getAttribute('width')) || image?.videoWidth;
  const height = Number(image?.getAttribute('height')) || image?.videoHeight;
  return width && height ? width / height : 16 / 9;
}

/** Stack uncropped photographs in columns whose outer edges end on the same line. */
function arrangePhotoMosaic(board, tiles, ratios, width, columns, gap, rowGap) {
  if (columns === 1 || tiles.length === 1) {
    board.classList.remove('is-mosaic');
    return;
  }

  // Four on a phone read better as a lead image and a trio than a cramped stack of three.
  const lead = columns === 2 && tiles.length === 4 ? 1 : 0;
  const count = columns >= 3 && (tiles.length === 4 || tiles.length >= 6) ? 3 : 2;
  const shares = count === 2 ? [.635, .365] : [.47, .315, .215];
  const available = width - (count - 1) * gap;
  const stacks = shares.map(() => ({ indices: [], depth: 0, estimated: 0 }));
  if (lead) {
    tiles[0].style.setProperty('--photo-column', '1 / -1');
    tiles[0].style.setProperty('--photo-row', '1');
    tiles[0].style.setProperty('--photo-offset', '0px');
  }
  ratios.forEach((ratio, i) => {
    if (i < lead) return;
    const column = stacks.reduce((shortest, stack, j) => stack.estimated < stacks[shortest].estimated ? j : shortest, 0);
    const stack = stacks[column];
    stack.indices.push(i);
    stack.depth += 1 / ratio;
    stack.estimated += available * shares[column] / ratio + rowGap;
  });

  // For a common height H, column width = (H - its fixed gaps) / sum(photo height / width).
  const reciprocal = stacks.reduce((sum, stack) => sum + 1 / stack.depth, 0);
  const gaps = stacks.reduce((sum, stack) => sum + (stack.indices.length - 1) * rowGap / stack.depth, 0);
  const height = (available + gaps) / reciprocal;
  const tracks = stacks.map((stack, column) => {
    const track = (height - (stack.indices.length - 1) * rowGap) / stack.depth;
    let offset = 0;
    for (const index of stack.indices) {
      const tile = tiles[index];
      tile.style.setProperty('--photo-column', String(column + 1));
      tile.style.setProperty('--photo-row', lead ? '2' : '1');
      tile.style.setProperty('--photo-offset', offset + 'px');
      offset += track / ratios[index] + rowGap;
    }
    return track + 'px';
  });
  board.style.setProperty('--photo-tracks', tracks.join(' '));
  board.classList.add('is-mosaic');
}

// Bring only the final covers to a common lower edge. Give their captions
// equal space so both the photographs and the card boxes finish together.
function balanceArchiveEnd(stacks) {
  const captionHeight = Math.max(...stacks.map(stack => stack.items.at(-1).caption));
  const ends = stacks.map(stack => {
    const last = stack.items.at(-1);
    last.padding = captionHeight - last.caption;
    const tail = stack.items.slice(-2);
    return { height: stack.height + last.padding, tail, flexible: tail.reduce((sum, item) => sum + item.cover, 0) };
  });
  const average = ends.reduce((sum, end) => sum + end.height, 0) / ends.length;
  // Limit how much any photograph is shortened, including in small filters.
  const target = Math.max(average, ...ends.map(end => end.height - end.flexible * .25));
  for (const end of ends) {
    const scale = 1 + (target - end.height) / end.flexible;
    for (const item of end.tail) item.cover *= scale;
  }
}

function arrangeArchive(board, tiles, ratios, width, columns, gap, rowGap) {
  const count = Math.min(columns, tiles.length);
  if (count === 1) {
    board.classList.remove('is-masonry');
    return;
  }
  board.style.setProperty('--archive-columns', count);
  board.classList.add('is-masonry');
  const columnWidth = (width - (count - 1) * gap) / count;
  const stacks = Array.from({ length: count }, () => ({ items: [], height: 0 }));
  tiles.forEach((tile, index) => {
    const media = tile.querySelector('.pin-media');
    const previousPadding = parseFloat(tile.style.getPropertyValue('--pin-bottom-padding')) || 0;
    const caption = tile.getBoundingClientRect().height - media.getBoundingClientRect().height - previousPadding;
    const item = { tile, caption, cover: columnWidth / ratios[index], padding: 0 };
    const stack = stacks.reduce((shortest, next) => shortest.height <= next.height ? shortest : next);
    stack.items.push(item);
    stack.height += item.cover + caption + rowGap;
  });
  // A load-more boundary is temporary. Keep every previous card in position
  // when the next batch arrives and square off only the end of the entire filter.
  if (!board.parentElement.dataset.next) balanceArchiveEnd(stacks);
  stacks.forEach((stack, index) => {
    let top = 0;
    for (const item of stack.items) {
      const style = item.tile.style;
      style.setProperty('--pin-column', index + 1);
      style.setProperty('--pin-top', top + 'px');
      style.setProperty('--pin-cover-height', item.cover + 'px');
      style.setProperty('--pin-bottom-padding', item.padding + 'px');
      top += item.cover + item.caption + rowGap;
    }
  });
}

/** Lay out each visible project board from its media dimensions and actual inner width. */
export function bindProjectRows() {
  if (typeof ResizeObserver === 'undefined') return;
  const states = new WeakMap();
  function arrange(board, force = false) {
    const style = getComputedStyle(board);
    const width = board.getBoundingClientRect().width - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
      - parseFloat(style.borderLeftWidth) - parseFloat(style.borderRightWidth);
    const columns = Number(style.getPropertyValue('--board-columns')) || 3;
    const gap = parseFloat(style.columnGap) || 0;
    const tiles = Array.from(board.children).filter(tile => !tile.hidden);
    const rowGap = parseFloat(style.rowGap) || 0;
    const state = [width, columns, gap, rowGap, tiles.length].join('/');
    if (width <= 0 || !tiles.length || (!force && state === states.get(board))) return;
    states.set(board, state);
    const ratios = tiles.map(aspectRatio);
    if (board.classList.contains('detail-gallery')) {
      arrangePhotoMosaic(board, tiles, ratios, width, columns, gap, rowGap);
      return;
    }
    arrangeArchive(board, tiles, ratios, width, columns, gap, rowGap);
  }

  const resize = new ResizeObserver(entries => entries.forEach(entry => arrange(entry.target)));
  const updates = new MutationObserver(entries => {
    const boards = new Set(entries.map(entry => entry.target));
    boards.forEach(board => arrange(board, true));
  });
  document.querySelectorAll('#view-projects .projects-grid, .project-detail-view .detail-gallery').forEach(board => {
    resize.observe(board);
    updates.observe(board, { childList: true });
    board.addEventListener('loadedmetadata', () => arrange(board, true), true);
  });
  document.fonts?.addEventListener('loadingdone', () => {
    document.querySelectorAll('#view-projects .projects-grid').forEach(board => arrange(board, true));
  });
  return arrange;
}
